import { AutocompleteWithLabel } from '@adotel/components'
import { useLazyQuery } from '@apollo/client'
import React, { useEffect } from 'react'

import { ACCOMMODATIONS_QUERY } from '../../graphql/accommodation'
import { TENANTS_QUERY } from '../../graphql/tenant'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import inventoryPagesRoleManagement from '../../staticRoleManagement/inventoryRoleManagement'
import { Accommodation } from '../../types/accommodation'
import { AllocationLogsInput } from '../../types/inventoryAllocationLogs'
import { Tenant } from '../../types/tenants'
import useStyles from './styles/AllocationHistoryBasicFilters.styles'

interface AllocationHistoryBasicFiltersProps {
  selectedAccommodation: any
  setselectedTenant: React.Dispatch<any>
  selectedTenant: any
  setselectedAccommodation: React.Dispatch<
    React.SetStateAction<
      | {
          label: string
          value: string
          code: string
        }
      | undefined
    >
  >

  setautoCompleteLabelRoomType: (v: {
    label: string
    value: string
    code: string
  }) => void
  setsecondaryFilterIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  allocationLogsInput: AllocationLogsInput
  setallocationLogsInput: React.Dispatch<
    React.SetStateAction<AllocationLogsInput>
  >
}

const AllocationHistoryBasicFilters = ({
  selectedTenant,
  setselectedTenant,
  setselectedAccommodation,
  setautoCompleteLabelRoomType,
  selectedAccommodation,
  setsecondaryFilterIsOpen,
  allocationLogsInput,
  setallocationLogsInput
}: AllocationHistoryBasicFiltersProps) => {
  const classes = useStyles()

  const hasAccessToChangeTenant = inventoryPagesRoleManagement
    .find((item) => item.route === '/inventory/:page')
    ?.sections?.SELECT_TENANT.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )

  const hasAccessToChangeAccommodation = inventoryPagesRoleManagement
    .find((item) => item.route === '/inventory/:page')
    ?.sections?.SELECT_ACCOMMODATION.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )

  const isTenant = inventoryPagesRoleManagement
    .find((item) => item.route === '/inventory/:page')
    ?.sections?.IS_TENANT.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )

  const [getTenants, { data: tenantsData, loading: loadingForTenantsList }] =
    useLazyQuery<{
      tenants: Tenant[]
    }>(TENANTS_QUERY)

  useEffect(() => {
    if (hasAccessToChangeTenant) {
      getTenants()
    }
  }, [])

  const [getAccommodations, { data: accommodationsData }] = useLazyQuery<{
    accommodations: Accommodation[]
  }>(ACCOMMODATIONS_QUERY, { notifyOnNetworkStatusChange: true })

  useEffect(() => {
    if (hasAccessToChangeAccommodation) {
      getAccommodations()
    }
  }, [])

  return (
    <div
      className={[classes.root, isTenant ? classes.rootForRoleTenant : ''].join(
        ' '
      )}>
      <div>
        {hasAccessToChangeTenant && (
          <AutocompleteWithLabel
            value={selectedTenant}
            label={getTranslate('Tenant')}
            placeholder={getTranslate('Select accommodation tenant')}
            includeInputInList
            onChange={(_, v: any) => {
              if (v) {
                setselectedTenant({
                  value: v.value,
                  label: v.label,
                  code: v.code
                })
                getAccommodations({
                  variables: {
                    tenantId: v.value
                  }
                })
              } else {
                setselectedTenant({
                  value: '',
                  label: '',
                  code: ''
                })
              }
              setselectedAccommodation({ code: '', label: '', value: '' })
              setautoCompleteLabelRoomType({ code: '', label: '', value: '' })
              setsecondaryFilterIsOpen(false)
            }}
            getOptionSelected={(option, value) =>
              option.value === value.value && option.label === value.label
            }
            getOptionLabel={(option) => option.label || ''}
            //TODO:Allocate hardcode remove filter later
            options={
              tenantsData?.tenants
                .filter((t) =>
                  ['101', '102', '858'].includes(
                    t.basicDetails.displayTenantCode || ''
                  )
                )
                .map((item) => ({
                  label: item.basicDetails.name,
                  value: item.basicDetails?.tenantId || '',
                  code: item.basicDetails?.tenantCode || ''
                })) || []
            }
            renderOption={(option) => <>{option.label}</>}
            size="small"
            inputSize="small"
            loading={loadingForTenantsList}
            loadingText={getTranslate('Loading') + '...'}
          />
        )}
      </div>
      <div>
        {hasAccessToChangeAccommodation && (
          <AutocompleteWithLabel
            label={getTranslate('Accommodation')}
            placeholder={getTranslate('Select accommodation')}
            includeInputInList
            onChange={(event, v: any) => {
              if (v) {
                setselectedAccommodation({
                  value: v.value,
                  label: v.label,
                  code: v.code
                })
                setallocationLogsInput({
                  ...allocationLogsInput,
                  propertyCode: v.code
                })
              } else {
                setselectedAccommodation({
                  value: '',
                  label: '',
                  code: ''
                })
                setallocationLogsInput({
                  ...allocationLogsInput,
                  propertyCode: ''
                })
              }
              setautoCompleteLabelRoomType({ code: '', label: '', value: '' })
              setsecondaryFilterIsOpen(false)
            }}
            getOptionSelected={(option, value) =>
              option.value === value.value && option.label === value.label
            }
            getOptionLabel={(option) => option.label || ''}
            //TODO:Allocate hardcode remove filter later
            options={
              accommodationsData
                ? accommodationsData?.accommodations.map((item) => ({
                    label: item.basicDetails.name,
                    value: item.basicDetails.propertyCode || '',
                    code: item.basicDetails.propertyCode,
                    id: item.basicDetails.accommodationID || ''
                  }))
                : []
            }
            value={selectedAccommodation}
            renderOption={(option) => <>{option.label}</>}
            size="small"
            inputSize="small"
            loading={loadingForTenantsList}
            loadingText={getTranslate('Please wait...')}
          />
        )}
      </div>
    </div>
  )
}

export default AllocationHistoryBasicFilters
